import React, { RefObject, useEffect, useRef, useState } from "react";
import { CALENDAR_ADD_ERROR_MESSAGE, CALENDAR_INVITES, DEFAULT_ERROR_MESSAGE } from "../../lib/constants";
import { useToast } from "../passport/NotificationWrapper/NotificationProvider";
import Icon from "../../components/_base/Icon";
import { CurrentUserResponseType, RegistrationSubscriptionParams } from "../../lib/api";
import Buttons from '../../components/button';
import { preRegisterToCalendarInvite, subscribeToCalendarInvite } from "../../lib/api/protected";
import './SubscribeCalendarButton.scss';
import { useCalendarInviteSubscription } from "../../hooks/use-calendar-subscription";

enum LoadingState {
  None,
  Subscribing,
  Unsubscribing,
}

interface SubscriptionCalendarButtonsProps {
  eventId: string;
  name: string;
  attendeeSubscribed: boolean;
  calendarInvite: CALENDAR_INVITES;
  data: CurrentUserResponseType | undefined;
  eventTitleForPassport?: string;
  downloadIcsFile: () => Promise<void>;
  openGoogleCalendar: () => Promise<void>;
  unSubscribeToCalendar: (registrationId: string) => Promise<void>;
  subscribeButtonLabel: string;
  subscribedButtonLabel: string;
  className: string;
  isSubscribeToEvent?: boolean;
  updateSubscribe: (subscribed: boolean) => void;
  meetingId?: string;
  isEventLevelSubscription?: boolean;
  withOptionsBoxStyle?: boolean;
  withCalendarIcon?: boolean;
  onShowDropDown?: (ref: RefObject<HTMLDivElement>) => void;
  customStyles?: React.CSSProperties;
  iconButton?: boolean;
  displayFullLabel?: boolean;
  meetingEndDateTime?: string;
  isSlider?: boolean;
  triggerSubscription?: (fn: () => Promise<void>) => void;
}

const SubscriptionCalendarButtons: React.FC<SubscriptionCalendarButtonsProps> = ({
  eventId,
  name,
  attendeeSubscribed,
  calendarInvite,
  data,
  eventTitleForPassport,
  downloadIcsFile,
  openGoogleCalendar,
  unSubscribeToCalendar,
  subscribeButtonLabel,
  subscribedButtonLabel,
  isSubscribeToEvent,
  className,
  updateSubscribe,
  meetingId,
  isEventLevelSubscription,
  withOptionsBoxStyle,
  withCalendarIcon,
  iconButton,
  onShowDropDown,
  customStyles,
  displayFullLabel,
  meetingEndDateTime,
  isSlider,
  triggerSubscription,
}) => {
  const { state, subscribe } = useCalendarInviteSubscription();
  const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.None);
  const { addToast } = useToast();
  const customClassName = `rounded on-hover-shadow flex ${isEventLevelSubscription ? 'mt-4' : ''} ${iconButton ? '' : 'h-10'}`;
  const iconSize = iconButton ? '25px' : '20px';
  const isButtonDisabled = loadingState !== LoadingState.None;
  const isMountedRef = useRef(true);
  const prevSubscribedRef = useRef<boolean | null>(null);
  const operationRef = useRef<LoadingState>(LoadingState.None);

  useEffect(() => {
    isMountedRef.current = true;

    const current = state.subscribed;
    const previous = prevSubscribedRef.current;

    if (current !== null && current !== previous) {
      handleSubscriptionResult(current);
      prevSubscribedRef.current = current;
    }

    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.subscribed]);

  useEffect(() => {
    if (state.error && operationRef.current !== LoadingState.None) {
      failOperation(operationRef.current === LoadingState.Subscribing ? state.error.message : DEFAULT_ERROR_MESSAGE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.error]);

  useEffect(() => {
    if (triggerSubscription) {
      triggerSubscription(handleSubscription);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerSubscription]);

  const failOperation = (message: string) => {
    operationRef.current = LoadingState.None;
    addToast(message, false, true);
    if (isMountedRef.current) {
      setLoadingState(LoadingState.None);
    }
  };

  const handleSubscriptionResult = async (subscribed: boolean) => {
    if (!subscribed && operationRef.current === LoadingState.Subscribing) {
      failOperation(CALENDAR_ADD_ERROR_MESSAGE);
      return;
    }
    if (subscribed) {
      addToast(eventTitleForPassport || name, true);
      await updateSubscribe(true);
    } else {
      await updateSubscribe(false);
    }
    operationRef.current = LoadingState.None;
    if (isMountedRef.current) {
      setLoadingState(LoadingState.None);
    }
  };

  const handleCalendarSubscription = async (isSubscribing: boolean): Promise<void> => {
    if (!data?.email) return;

    operationRef.current = isSubscribing ? LoadingState.Subscribing : LoadingState.Unsubscribing;
    prevSubscribedRef.current = null;
    setLoadingState(operationRef.current);

    try {
      const subscriptionResponse = await preRegisterToCalendarInvite({
        attendeeEmail: data.email,
        eventId: eventId.toString(),
        meetingId: meetingId,
      });

      if (!subscriptionResponse?.registrationId || !subscriptionResponse?.appSyncToken) {
        failOperation(isSubscribing ? CALENDAR_ADD_ERROR_MESSAGE : DEFAULT_ERROR_MESSAGE);
        return;
      }

      await updateData(subscriptionResponse, isSubscribing);
    } catch (error) {
      failOperation(DEFAULT_ERROR_MESSAGE);
    }
  };

  const handleSubscription = async (): Promise<void> => {
    setLoadingState(LoadingState.Subscribing);
    await handleCalendarSubscription(true);
  };

  const updateData = async (data: RegistrationSubscriptionParams, subscribeCalendar: boolean): Promise<void> => {
    try {
      if (data){
        await subscribe({ registrationId: data.registrationId, appSyncToken: data.appSyncToken });
        subscribeCalendar ? await subscribeToCalendarInvite(data.registrationId) : await unSubscribeToCalendar(data.registrationId);
      }
    } catch (error) {
      failOperation(DEFAULT_ERROR_MESSAGE);
    }
  };

  const handleUnsubscribe = async (): Promise<void> => {
    await handleCalendarSubscription(false);
  };

  if (meetingEndDateTime && new Date(meetingEndDateTime).getTime() < new Date().getTime()){
    return null;
  }

  return (
    <div className={`${iconButton ? 'calendar-container' : ''} flex justify-between`}>
      {calendarInvite !== CALENDAR_INVITES.Download &&
        (attendeeSubscribed && loadingState !== LoadingState.Unsubscribing
          ? (
            <Buttons.OptionsButton
              data-test-id="subscribed-calendar-invite"
              label={subscribedButtonLabel}
              name={subscribedButtonLabel}
              disabled={isButtonDisabled}
              eventId={eventId}
              eventName={name}
              className={`${className} outline-none on-hover-shadow flex items-center pt-px ${isEventLevelSubscription ? 'mt-4' : ''}`}
              unSubscribeToCalendar={handleUnsubscribe}
              downloadIcsFile={downloadIcsFile}
              openGoogleCalendar={openGoogleCalendar}
              isSubscribeToEvent={isSubscribeToEvent}
              calendarInvite={calendarInvite}
              isSubscribed
              withOptionsBoxStyle={withOptionsBoxStyle}
              iconButton={iconButton}
              onShowDropDown={onShowDropDown}
              customStyles={customStyles}
              isSlider={isSlider}
            />
          )
          : loadingState === LoadingState.Subscribing
            ? (
              <Buttons.Common
                name="Adding to Calendar"
                className={`subscribe-processing-btn ${customClassName}`}
              >
                <span className="preview-spinner add-spin animate-spin" />
                <label className="font-size-11px loading-label">
                  {isEventLevelSubscription || displayFullLabel ? 'Adding to Calendar...' : 'Adding'}
                </label>
              </Buttons.Common>
            )
            : loadingState === LoadingState.Unsubscribing
              ? (
                <Buttons.Common
                  name="Removing from Calendar"
                  className={`subscribe-processing-btn ${customClassName}`}
                >
                  <span className="preview-spinner animate-spin" />
                  <label className="font-size-11px loading-label">
                    {isEventLevelSubscription || displayFullLabel ? 'Removing from Calendar...' : 'Removing'}
                  </label>
                </Buttons.Common>
              )
              : (
                <Buttons.Common
                  name={subscribeButtonLabel}
                  className={`${iconButton ? 'p-2 pr-0' : 'editable-welcome--subscribe-title'} ${customClassName}`}
                  data-test-id="subscribe-calendar-invite"
                  onClick={handleSubscription}
                  disabled={isButtonDisabled}
                >
                  <Icon.AddToCalendar
                    width={iconSize}
                    height={iconSize}
                    viewBox="0 0 50 50"
                    className="add-calendar-icon fill-primary col order-first mr-2"
                  />
                  {!iconButton && <label className="text-primary">{subscribeButtonLabel}</label>}
                </Buttons.Common>
              )
        )
      }
      {calendarInvite === CALENDAR_INVITES.Download &&
        <div className={`${isEventLevelSubscription ? ' my-6 ml-5' : ''}`}>
          <Buttons.OptionsButton
            label={isEventLevelSubscription ? 'Add to Calendar' : '+ Add to Calendar'}
            name="addToCalendar"
            disabled={isButtonDisabled}
            eventId={eventId}
            eventName={name}
            className={`rounded on-hover-shadow flex ${isEventLevelSubscription ? 'connect-to-meeting-btn bg-primary text-white' : ''}`}
            downloadIcsFile={downloadIcsFile}
            openGoogleCalendar={openGoogleCalendar}
            withCalendarIcon={withCalendarIcon}
            withOptionsBoxStyle={withOptionsBoxStyle}
            iconButton={iconButton}
            onShowDropDown={onShowDropDown}
            customStyles={customStyles}
            isSlider={isSlider}
          />
        </div>
      }
    </div>
  );
};

export default SubscriptionCalendarButtons;
